import { useState } from 'react';
import { useParams } from 'react-router';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import PlantOverviewAdvancedAndBasicPage from '@/features/plants/components/plant-overview/production-and-storage/plant-production-and-storage-overview-advanced-and-basic-page.tsx';
import PlantStorageOnlyOverviewPage from '@/features/plants/components/plant-overview/storage-only/plant-storage-only-overview-page.tsx';
import {
  Credenza,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from '@/components/ui/credenza.tsx';
import { Button } from '@/components/ui/button.tsx';
import { TriangleAlert } from 'lucide-react';

export function Plant() {
  const { plantId } = useParams();
  const [isDismissed, setIsDismissed] = useState(false);

  const { data: plant } = useGetPlantQuery({
    plantId: plantId,
  });

  const isMaintenanceModalOpen = plant?.basicDetails.status === 'Maintenance' && !isDismissed;

  return (
    <>
      <PlantOverview plantId={plantId} setupType={plant?.technicalSpecifications?.setupType} />
      <MaintenanceModal open={isMaintenanceModalOpen} onDismiss={() => setIsDismissed(true)} />
    </>
  );
}

function PlantOverview({
  plantId,
  setupType,
}: {
  plantId: string | undefined;
  setupType?: string;
}) {
  if (setupType === 'storage-only') {
    return <PlantStorageOnlyOverviewPage plantId={plantId} />;
  }

  return <PlantOverviewAdvancedAndBasicPage plantId={plantId} />;
}

function MaintenanceModal({ open, onDismiss }: { open: boolean; onDismiss: () => void }) {
  return (
    <Credenza open={open} onOpenChange={(isOpen) => !isOpen && onDismiss()}>
      <CredenzaContent>
        <CredenzaHeader>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <TriangleAlert className="h-5 w-5 text-amber-600" />
            </div>
            <CredenzaTitle>Plant in maintenance mode.</CredenzaTitle>
          </div>
          <CredenzaDescription className="mt-2 text-base">
            Our team is addressing a known issue. Operations will resume once resolved.
          </CredenzaDescription>
        </CredenzaHeader>
        <CredenzaFooter>
          <Button onClick={onDismiss}>OK</Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
}

export default Plant;
